import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import api from '../../../utils/api'
import SortableItem from './SortableItem'
import MetadataForm from './MetadataForm'
import SongSearch from './SongSearch'

const PlaylistEditor = ({ isNew = false }) => {
  const { playlistId } = useParams()
  const navigate = useNavigate()

  // State for playlist data
  const [title, setTitle] = useState('')
  const [songs, setSongs] = useState([])
  const [users, setUsers] = useState([])
  const [service, setService] = useState('')
  const [serviceId, setServiceId] = useState('')

  // UI states
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // State for position editing
  const [editingPosition, setEditingPosition] = useState(null)
  const [tempPosition, setTempPosition] = useState('')

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    })
  )

  const fetchPlaylistData = async () => {
    if (isNew) return

    try {
      setLoading(true)
      setError(null)

      const response = await api.get(`admin/playlists/${playlistId}`)

      setTitle(response.data.title)
      setSongs(response.data.songs || [])
      setUsers(response.data.users || [])
      setService(response.data.service)
      setServiceId(response.data.serviceId)
    } catch (err) {
      console.error('Error fetching playlist:', err)
      setError(`Failed to load playlist: ${err.response?.data?.error || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isNew) return
    fetchPlaylistData()
  }, [playlistId, isNew])

  const startEditingPosition = (songId, currentPosition) => {
    setEditingPosition(songId)
    setTempPosition(currentPosition.toString())
  }

  const savePosition = async (song) => {
    const newPosition = parseInt(tempPosition, 10)
    const totalSongs = songs.length

    if (isNaN(newPosition) || newPosition < 1 || newPosition > totalSongs) {
      setError(`Position must be a number between 1 and ${totalSongs}`)
      return
    }

    try {
      setLoading(true)

      await api.post(`admin/playlists/${playlistId}/reorder-song`, {
        songId: song.id,
        newPosition,
        adjustOthers: false,
      })

      await fetchPlaylistData()

      setSuccess('Song position updated')
      setTimeout(() => setSuccess(null), 2000)
    } catch (err) {
      console.error('Error updating position:', err)
      setError(`Failed to update position: ${err.message}`)
      fetchPlaylistData()
    } finally {
      setLoading(false)
      setEditingPosition(null)
      setTempPosition('')
    }
  }

  const removeSong = async (songId) => {
    try {
      setLoading(true)

      await api.post(`admin/playlists/${playlistId}/remove-song`, { songId })

      setSongs(songs.filter((song) => song.id !== songId))

      setSuccess('Song removed successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error removing song:', err)
      setError(`Failed to remove song: ${err.response?.data?.error || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  const savePlaylist = async (e) => {
    e.preventDefault()

    try {
      setSaving(true)

      if (isNew) {
        const response = await api.post('admin/playlists/create', { title })

        setSuccess('Playlist created successfully')
        setTimeout(() => {
          navigate(`/admin/playlist/edit/${response.data.playlistId}`)
        }, 1500)
      } else {
        await api.post(`admin/playlists/${playlistId}/update`, { title })

        setSuccess('Playlist updated successfully')
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err) {
      console.error('Error saving playlist:', err)
      setError(`Failed to save playlist: ${err.response?.data?.error || 'Unknown error'}`)
    } finally {
      setSaving(false)
    }
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event

    if (!active || !over || active.id === over.id) {
      return
    }

    try {
      setLoading(true)

      const sortedSongs = [...songs].sort((a, b) => a.position - b.position)

      const oldIndex = sortedSongs.findIndex((song) => song.id.toString() === active.id)
      const newIndex = sortedSongs.findIndex((song) => song.id.toString() === over.id)

      if (oldIndex === -1 || newIndex === -1) return

      const draggedSong = sortedSongs[oldIndex]
      const targetSong = sortedSongs[newIndex]

      await api.post(`admin/playlists/${playlistId}/reorder-song`, {
        songId: draggedSong.id,
        newPosition: targetSong.position,
        adjustOthers: true,
      })

      const reordered = arrayMove(sortedSongs, oldIndex, newIndex)
      const updatedSongs = reordered.map((song, idx) => ({
        ...song,
        position: idx + 1,
      }))

      setSongs(updatedSongs)

      setSuccess('Song order updated')
      setTimeout(() => setSuccess(null), 2000)
    } catch (err) {
      console.error('Error reordering song:', err)
      setError(`Failed to reorder song: ${err.message}`)
      fetchPlaylistData()
    } finally {
      setLoading(false)
    }
  }

  const handleSongAdded = (song) => {
    setSongs([...songs, { ...song, position: songs.length + 1 }])
    setSuccess('Song added successfully')
    setTimeout(() => setSuccess(null), 3000)
  }

  return (
    <div>
      <div className='mb-6 flex items-center space-x-2'>
        <Link to='/admin/playlist' className='bg-gray-200 hover:bg-gray-300 rounded px-3 py-1'>
          &larr; Back to Playlists
        </Link>
        <h2 className='text-xl font-semibold'>
          {isNew ? 'Create New Playlist' : `Edit Playlist: ${title}`}
        </h2>
      </div>

      {error && <div className='bg-red-100 text-red-700 mb-6 rounded p-4'>{error}</div>}
      {success && <div className='bg-green-100 text-green-700 mb-6 rounded p-4'>{success}</div>}

      {loading && !songs.length ? (
        <div className='p-4 text-center'>Loading playlist data...</div>
      ) : (
        <>
          <MetadataForm
            title={title}
            setTitle={setTitle}
            service={service}
            serviceId={serviceId}
            users={users}
            isNew={isNew}
            saving={saving}
            onSubmit={savePlaylist}
          />

          {!isNew && (
            <>
              <SongSearch
                playlistId={playlistId}
                existingSongIds={songs.map((s) => s.id)}
                onSongAdded={handleSongAdded}
                loading={loading}
                setLoading={setLoading}
                setError={setError}
              />

              <div className='mb-6'>
                <h3 className='mb-3 text-lg font-semibold'>Playlist Songs ({songs.length})</h3>

                {songs.length === 0 ? (
                  <div className='rounded border p-4 text-center'>
                    No songs in this playlist. Use the search above to add songs.
                  </div>
                ) : (
                  <div className='overflow-hidden rounded border'>
                    <div className='bg-gray-100 grid grid-cols-12 px-4 py-2 font-semibold'>
                      <div className='col-span-1'>#</div>
                      <div className='col-span-5'>Title</div>
                      <div className='col-span-4'>Artists</div>
                      <div className='col-span-2 text-right'>Actions</div>
                    </div>

                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={songs.map((song) => song.id.toString())}
                        strategy={verticalListSortingStrategy}
                      >
                        {[...songs]
                          .sort((a, b) => a.position - b.position)
                          .map((song) => (
                            <SortableItem
                              key={song.id}
                              id={song.id.toString()}
                              song={song}
                              onRemove={removeSong}
                              loading={loading}
                              onEdit={startEditingPosition}
                              editingPosition={editingPosition}
                              tempPosition={tempPosition}
                              setTempPosition={setTempPosition}
                              onSavePosition={savePosition}
                            />
                          ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default PlaylistEditor
